/** @jsxImportSource preact */

import * as m from "../paraglide/messages.js";
import { WIDGET_KEYFRAMES } from "../ui/animations";
import type { WidgetTheme } from "../ui/theme";
import { CloseIcon } from "../ui/icons";

/**
 * 覆盖层内部子元素的层叠值。压过主题普通弹层靠它，但原生 modal dialog
 * 在 top layer——文档内 z-index 再大也越不过去，抬层由挂载侧用
 * manual popover 完成；这个值继续在 top layer 成员间和回落渲染时排序。
 */
export const OVERLAY_Z_INDEX = 2_147_483_000;

/**
 * 拾取覆盖层的视图状态。pick/confirm 都可以选中元素；closed 只留引导文案，
 * 等商家关掉——令牌已死，继续悬停选中没有意义。
 */
export type PickerView =
  | { phase: "pick"; prompt: string }
  | {
      phase: "confirm";
      prompt: string;
      selector: string;
      saving: boolean;
      failure?: "network" | "selector";
    }
  | { phase: "closed"; tone: "invalid" | "consumed" | "gone" };

export interface PickerOverlayProps {
  view: PickerView;
  theme: WidgetTheme;
  font: string;
  fontSize: string;
  onExit: () => void;
  onReselect: () => void;
  onSave: () => void;
}

/**
 * 拾取模式的底部控制条。渲染在挂件 Shadow DOM 里，主题色借用挂件外观；
 * 不碰宿主元素。选中高亮框由控制器直接维护，不走这里。
 */
export function PickerOverlay({
  view,
  theme,
  font,
  fontSize,
  onExit,
  onReselect,
  onSave,
}: PickerOverlayProps) {
  const closeButton = (
    <button
      aria-label={m.widget_picker_exit()}
      onClick={onExit}
      style={{
        background: "transparent",
        border: 0,
        borderRadius: theme.ctrlRadius,
        color: theme.muted,
        cursor: "pointer",
        display: "grid",
        flex: "none",
        height: 28,
        padding: 0,
        placeItems: "center",
        width: 28,
      }}
      type="button"
    >
      <CloseIcon size={16} />
    </button>
  );
  const actionStyle = (primary: boolean) =>
    ({
      background: primary ? theme.accentFill : "transparent",
      border: primary ? 0 : `1px solid ${theme.line}`,
      borderRadius: theme.ctrlRadius,
      color: primary ? theme.accentFillFg : theme.ink,
      cursor: "pointer",
      font: "inherit",
      fontSize: ".9em",
      fontWeight: 600,
      padding: "6px 14px",
    }) as const;
  const ellipsisStyle = {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  } as const;

  return (
    <div
      aria-label={m.widget_picker_region()}
      role="region"
      style={{
        alignItems: "center",
        animation: "gnPanelIn .25s cubic-bezier(.2,.8,.2,1) both",
        background: theme.panelBg,
        border: `1px solid ${theme.line}`,
        borderRadius: theme.panelRadius,
        bottom: 24,
        boxShadow: "0 12px 32px rgba(0,0,0,.18)",
        boxSizing: "border-box",
        color: theme.ink,
        display: "flex",
        fontFamily: font,
        fontSize,
        gap: 12,
        // 居中用 left/right 0 + auto 外边距，不走 transform——gnPanelIn
        // 的末帧 transform:none 会把 translateX(-50%) 永久盖掉。
        left: 0,
        lineHeight: 1.45,
        margin: "0 auto",
        maxWidth: "min(560px, calc(100vw - 32px))",
        padding: "10px 12px 10px 16px",
        position: "fixed",
        right: 0,
        width: "fit-content",
        zIndex: OVERLAY_Z_INDEX,
      }}
    >
      {/* 聊天关闭时 WidgetRoot 不渲染，keyframes 由拾取条自己带上。 */}
      <style>{WIDGET_KEYFRAMES}</style>
      {view.phase === "pick" && (
        <div style={{ minWidth: 0 }}>
          {view.prompt && (
            <div
              style={{
                color: theme.muted,
                fontSize: ".85em",
                ...ellipsisStyle,
              }}
            >
              {m.widget_picker_for_rule({ prompt: view.prompt })}
            </div>
          )}
          <div>{m.widget_picker_hint()}</div>
        </div>
      )}
      {view.phase === "confirm" && (
        <div style={{ minWidth: 0 }}>
          <div>{m.widget_picker_confirm()}</div>
          <code
            style={{
              color: theme.muted,
              display: "block",
              fontSize: ".82em",
              ...ellipsisStyle,
            }}
          >
            {view.selector}
          </code>
          {view.failure && (
            <div style={{ color: theme.dangerText, fontSize: ".85em" }}>
              {view.failure === "selector"
                ? m.widget_picker_selector_rejected()
                : m.widget_picker_failed()}
            </div>
          )}
        </div>
      )}
      {view.phase === "closed" && (
        <div style={{ minWidth: 0 }}>
          {view.tone === "consumed"
            ? m.widget_picker_consumed()
            : view.tone === "gone"
              ? m.widget_picker_rule_gone()
              : m.widget_picker_invalid()}
        </div>
      )}
      {view.phase === "confirm" && (
        <div style={{ display: "flex", flex: "none", gap: 8 }}>
          {view.failure === "network" && (
            <button onClick={onSave} style={actionStyle(false)} type="button">
              {m.widget_picker_retry()}
            </button>
          )}
          <button onClick={onReselect} style={actionStyle(false)} type="button">
            {m.widget_picker_reselect()}
          </button>
          <button
            disabled={view.saving}
            onClick={onSave}
            style={actionStyle(true)}
            type="button"
          >
            {view.saving ? m.widget_picker_saving() : m.widget_picker_save()}
          </button>
        </div>
      )}
      {closeButton}
    </div>
  );
}
